// ============================================================================
// TrendsPanel — Personal Finance V2 (Blueprint §5.2)
// Seção "📈 Tendências" — médias históricas e variação mensal por categoria.
// Baseado no YNAB Reflect Tab (Spending Trends, Income vs Expense)
// ============================================================================

import React from "react";

const formatCurrency = (val) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(val);

const TREND_ICONS = {
  up: "▲",
  down: "▼",
  stable: "—",
};

const TREND_COLORS = {
  up: "text-red-600",
  down: "text-emerald-600",
  stable: "text-slate-400",
};

export function TrendsPanel({ trendsData, categories }) {
  const { averages, last12Months, totalExpenses, overAllAvg, totalMonths } =
    trendsData || {};

  if (!averages) {
    return (
      <div className="p-8 text-center text-gray-400 text-sm">
        Nenhum dado de tendências disponível.
      </div>
    );
  }

  const groupEntries = Object.entries(averages).filter(
    ([key]) => key !== "RECEITAS" && key !== "FAMILIA",
  );

  return (
    <div className="space-y-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">
          📈 Gastos — Últimos {totalMonths} Meses
        </h3>
        <span className="text-[11px] text-slate-500">
          Total: {formatCurrency(totalExpenses)} | Média/mês:{" "}
          {formatCurrency(overAllAvg)}
        </span>
      </div>

      {/* Grid de cards por categoria */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {groupEntries.map(([key, data]) => {
          const cat = categories?.[key] || {};
          // Mesma correção do DashboardCards.jsx (17/09/2026): só extrai
          // "emoji" se for pictográfico de verdade, senão duplica o nome do
          // grupo pra grupos criados sem emoji (ex: via combobox novo).
          const rawLabel = cat.label || key;
          const emojiMatch = rawLabel.match(/^\p{Extended_Pictographic}+\s*/u);
          const emoji = emojiMatch ? emojiMatch[0].trim() : "";
          const cleanLabel = (
            emojiMatch ? rawLabel.slice(emojiMatch[0].length) : rawLabel
          ).trim();
          const momText =
            data.momChange > 0 ? `+${data.momChange}%` : `${data.momChange}%`;
          const hasBudget = data.budget > 0;
          const budgetPercent =
            hasBudget && data.avg > 0
              ? Math.round((data.avg / data.budget) * 100)
              : 0;
          const isOver = budgetPercent > 100;

          return (
            <div
              key={key}
              className="bg-white rounded-md border border-slate-200 px-3 py-2.5 flex flex-col gap-1.5 hover:shadow-sm transition-shadow"
            >
              {/* Linha 1: Label + trend */}
              <div className="flex items-center justify-between">
                <span className="text-[10.5px] font-semibold text-slate-600 uppercase tracking-wide truncate">
                  <span className="mr-1">{emoji}</span>
                  {cleanLabel}
                </span>
                <span
                  className={`text-[10px] font-bold tabular-nums ${TREND_COLORS[data.trend]}`}
                  title={`Variação vs mês anterior: ${momText}`}
                >
                  {TREND_ICONS[data.trend]} {momText}
                </span>
              </div>

              {/* Linha 2: Média mensal */}
              <div className="flex items-baseline gap-1">
                <span className="font-mono font-bold text-[14px] text-slate-800 tabular-nums">
                  {formatCurrency(data.avg)}
                </span>
                <span className="text-[9px] text-slate-400">/mês</span>
              </div>

              {/* Linha 3: Min / Max */}
              <div className="flex items-center gap-2 text-[9px] text-slate-400">
                <span>min {formatCurrency(data.min)}</span>
                <span>•</span>
                <span>máx {formatCurrency(data.max)}</span>
              </div>

              {/* Linha 4: Budget vs Average */}
              {hasBudget && (
                <div className="flex items-center justify-between text-[9px]">
                  <span className="text-slate-500">
                    meta {formatCurrency(data.budget)}
                  </span>
                  <span
                    className={`font-semibold ${
                      isOver ? "text-red-600" : "text-emerald-600"
                    }`}
                  >
                    {budgetPercent}%
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Tabela de evolução mensal */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-200">
          <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wide">
            Evolução Mensal
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-2 py-1 text-left text-[9px] font-semibold text-slate-500 uppercase">
                  Mês
                </th>
                {groupEntries.map(([key]) => (
                  <th
                    key={key}
                    className="px-2 py-1 text-right text-[9px] font-semibold text-slate-500 uppercase"
                  >
                    {key}
                  </th>
                ))}
                <th className="px-2 py-1 text-right text-[9px] font-semibold text-slate-500 uppercase">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {[...(last12Months || [])].reverse().map((month) => (
                <tr
                  key={month.ym}
                  className="border-b border-slate-100 hover:bg-slate-50"
                >
                  <td className="px-2 py-1 font-semibold text-slate-600">
                    {month.label}
                  </td>
                  {groupEntries.map(([key]) => (
                    <td
                      key={key}
                      className="px-2 py-1 text-right font-mono tabular-nums text-slate-700"
                    >
                      {formatCurrency(month.byGroup[key] || 0)}
                    </td>
                  ))}
                  <td className="px-2 py-1 text-right font-mono tabular-nums font-bold text-slate-800">
                    {formatCurrency(month.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Rodapé com totais */}
      <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 flex items-center gap-4 text-[11px]">
        <span className="font-semibold text-slate-600">Resumo:</span>
        <span className="text-slate-500">
          Média geral:{" "}
          <strong className="text-slate-700">
            {formatCurrency(overAllAvg)}
          </strong>
        </span>
        <span className="text-slate-500">
          Total 12 meses:{" "}
          <strong className="text-slate-700">
            {formatCurrency(totalExpenses)}
          </strong>
        </span>
      </div>
    </div>
  );
}
